import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  DialogTitle,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  makeStyles,
  Theme,
  Typography
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import { EQuotePurchaserType, EQuoteType, IErrors } from "../../api/Types";
import { QuoteDeduction } from "../../api/Quote/QuoteDeduction";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { Quote } from "../../api/Quote/Quote";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { deleteQuoteDeduction, deleteQuoteItem, updateQuoteDeduction, updateQuoteItem } from "../../api/SwyftApi";
import { syncQuote } from "../../store/QuoteSlice";
import { colors } from "swyft-react-core";
import { quoteDeductionValue } from "../../app/utils";
import { AdditionalItemsCode, SolarBusinessAgriVatRate } from "../../app/consts";
import { TextField } from "@material-ui/core";
import { toast } from "react-toastify";
import { PriceFormat } from "../../components/typography/PriceFormat";

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    marginBottom: theme.spacing(2)
  },
  green: {
    color: "#178a17"
  },
  red: {
    color: "#F00"
  },
  delete: {
    backgroundColor: "#F00",
    color: colors.white
  },
  button: {
    margin: theme.spacing(1)
  }
}));

interface Props {
  quote?: Quote;
  item?: QuoteLineItem;
  deduction?: QuoteDeduction;
  isOpen: boolean;
  onClose: () => void;
  totalPrice: number;
}

interface State {
  newPrice: number;
  newTotal: number;
  priceInput: string;
  description: string;
}

const defaultProps = {
  isOpen: false,
  onClose: () => {},
  quote: undefined,
  item: undefined,
  deduction: undefined,
  totalPrice: 0
};

export const LineItemEditModal = (props: Props = defaultProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [state, setState] = useState<State>({
    newPrice: 0,
    newTotal: 0,
    priceInput: "",
    description: ""
  });

  const { isOpen, onClose, quote, item, deduction, totalPrice } = props;

  var includeVat = props.quote?.type !== EQuoteType.Solar || props.quote?.purchaserType === EQuotePurchaserType.Home;

  const itemPrice = (item: QuoteLineItem, includeVat: boolean = true): number => {
    if (includeVat) {
      return item.price;
    }

    let price = item.price / (1 + SolarBusinessAgriVatRate / 100);
    return Math.round((price + Number.EPSILON) * 100) / 100;
  };

  useEffect(() => {
    setState({
      newPrice: (item && itemPrice(item, includeVat)) || (deduction && quoteDeductionValue(deduction, includeVat)) || 0,
      newTotal: totalPrice,
      priceInput:
        String((item && itemPrice(item, includeVat)) || (deduction && quoteDeductionValue(deduction, includeVat))) ||
        "",
      description: (item && item.description) || (deduction && deduction.description) || ""
    });
  }, [item, deduction, totalPrice, includeVat]);

  if (item == null && deduction == null) {
    return <React.Fragment />;
  }

  const changePrice = function (input: string) {
    var originalPrice = item ? item.price : deduction ? quoteDeductionValue(deduction, includeVat) : 0;

    var value = input.replace(/[^0-9-.]/g, "");

    // remove and duplicate or non-first-character minus
    if (value.indexOf("-") >= 0) {
      value = `-${value.replace("-", "")}`;
    }
    // remove any leading zeros
    while (value.startsWith("0")) {
      value = value.substring(1);
    }
    // Duplicate decimal points
    if (value.indexOf(".") !== value.lastIndexOf(".")) {
      value = value.substring(0, value.indexOf(".") + 1) + value.substring(value.indexOf(".")).replace(/\./g, "");
    }
    // trim to 2 significant bits
    var dotIndex = value.indexOf(".");
    if (dotIndex > 0 && value.length > dotIndex + 3) {
      value = value.substring(0, dotIndex + 3);
    }

    var newPrice: number = Number(value.endsWith(".") ? `${value}0` : value);

    setState({
      ...state,
      newPrice: newPrice,
      newTotal: totalPrice - originalPrice + newPrice,
      priceInput: value
    });
  };

  const handleSave = async () => {
    if (item) {
      let price = state.newPrice;
      if (props.quote?.type === EQuoteType.Solar && props.quote?.purchaserType !== EQuotePurchaserType.Home) {
        price += price * (SolarBusinessAgriVatRate / 100);
      }

      const lineItem = {
        price: price,
        description: state.description
      } as QuoteLineItem;

      await updateQuoteItem(quote!.id, item!.productId, lineItem)
        .then(() => {
          dispatch(syncQuote(quote!.id, false));
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
        });
    } else if (deduction) {
      let discountValue = Math.abs(state.newPrice);
      if (props.quote?.type === EQuoteType.Solar && props.quote?.purchaserType !== EQuotePurchaserType.Home) {
        discountValue += discountValue * (SolarBusinessAgriVatRate / 100);
      }

      const quoteDeduction = {
        value: discountValue,
        description: state.description
      } as QuoteDeduction;

      await updateQuoteDeduction(quote!.id, deduction!.id, quoteDeduction)
        .then(() => {
          dispatch(syncQuote(quote!.id, false));
        })
        .catch((err) => {
          console.log(err);
          const errors: IErrors = {};
          Object.keys(err.errors).forEach((key: string) => {
            const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
            errors[fieldName] = err.errors[key][0];
          });
          throw errors;
        });
    }
  };

  const handleDelete = async () => {
    var deleteFunc = async (quoteId: string, id: string) => {
      return item ? deleteQuoteItem(quoteId, id) : deleteQuoteDeduction(quoteId, id);
    };

    await deleteFunc(quote!.id, item ? item!.productId : deduction!.id)
      .then(() => {
        dispatch(syncQuote(quote!.id, false));
      })
      .catch((err) => {
        console.log(err);
        const errors: IErrors = {};
        Object.keys(err.errors).forEach((key: string) => {
          const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
          errors[fieldName] = err.errors[key][0];
        });
        throw errors;
      });
  };

  const showAmountEdit = (deduction?.isCustomValue ?? false) || (!!item ?? false);

  return !item && !deduction ? (
    <div></div>
  ) : (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="line-item-edit"
      aria-describedby="Edit line item"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="edit-item">Editing Item: {item ? item.productCode : deduction!.code}</DialogTitle>

      <Container maxWidth="lg">
        <Typography variant="body2" paragraph>
          {item ? item.name : deduction!.name}
        </Typography>
        <Box pb={3} minWidth="450px">
          <form noValidate autoComplete="off">
            {item && (
              <TextField
                className={classes.margin}
                fullWidth
                id="description"
                label="Description"
                multiline
                maxRows={10}
                value={state.description}
                onChange={(e) => setState({ ...state, description: e.currentTarget.value })}
              />
            )}
            {deduction && deduction.code === "D0000" && (
              <TextField
                className={classes.margin}
                fullWidth
                id="description"
                label="Description"
                multiline
                maxRows={10}
                value={state.description}
                onChange={(e) => setState({ ...state, description: e.currentTarget.value })}
              />
            )}
            <Collapse in={showAmountEdit}>
              <FormControl fullWidth className={classes.margin}>
                <InputLabel htmlFor="standard-adornment-amount">Amount</InputLabel>
                <Input
                  id="standard-adornment-amount"
                  value={state.priceInput}
                  onChange={
                    (e) => {
                      changePrice(e.target.value);
                    }
                    // (e) => changePrice(Number(e.target.value) || 0)
                  }
                  startAdornment={<InputAdornment position="start">€</InputAdornment>}
                />
              </FormControl>
            </Collapse>
            {item && (
              <div>
                <Typography variant="body2" paragraph>
                  Quantity: {item.quantity}
                </Typography>

                <Typography variant="body2" paragraph>
                  Line Price: {state.newPrice * item.quantity}
                </Typography>
              </div>
            )}

            <Box display="flex" justifyContent="space-between">
              <Typography variant="h2" paragraph>
                Current Total:
                <PriceFormat value={totalPrice} />
              </Typography>

              <Box ml={2}>
                <Typography variant="h2">
                  Updated Total:
                  <PriceFormat value={state.newTotal} />
                </Typography>
                {state.newTotal > props.totalPrice && (
                  <Box display="flex" justifyContent="flex-end">
                    <Typography variant="subtitle2" className={classes.green}>
                      <PriceFormat value={state.newTotal - props.totalPrice} />
                    </Typography>
                    <ArrowUpwardIcon className={classes.green} />
                  </Box>
                )}
                {state.newTotal < props.totalPrice && (
                  <Box display="flex" justifyContent="flex-end">
                    <Typography variant="subtitle2" className={classes.red}>
                      -
                      <PriceFormat value={props.totalPrice - state.newTotal} />
                    </Typography>
                    <ArrowDownwardIcon className={classes.red} />
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="center">
              {(deduction || (item && item.productCode === AdditionalItemsCode)) && (
                <Button
                  variant="contained"
                  onClick={(_) => handleDelete()}
                  className={`${classes.button} ${classes.delete}`}
                >
                  Remove From Quote
                </Button>
              )}
              <Button variant="contained" color="secondary" onClick={(_) => handleSave()} className={classes.button}>
                Save Changes
              </Button>
              <Button variant="outlined" color="secondary" onClick={(_) => onClose()} className={classes.button}>
                Cancel
              </Button>
            </Box>
          </form>
        </Box>
      </Container>
    </Dialog>
  );
};
